import { useState, useRef } from "react";
import { useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ColourSwatches } from "@/components/ColourSwatches";
import { findProfile, FINISHES, PANEL_DISCLAIMER, crossSection, sampleDetail, finishImage } from "@/lib/panels";

const SpecRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-6 border-b border-border/60 py-4">
    <dt className="text-[0.72rem] uppercase tracking-[0.16em] text-muted-foreground">{label}</dt>
    <dd className="text-right text-sm tracking-editorial">{value}</dd>
  </div>
);

export default function PanelDetail() {
  const { profile } = useParams();
  const data = findProfile(profile);
  const formRef = useRef(null);

  const [finishIdx, setFinishIdx] = useState(0);
  const selectedFinish = FINISHES[finishIdx];
  const finish = selectedFinish.name;
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    quantity: "", location: "", notes: "",
  });

  if (!data) return <Navigate to="/collections/timber/architectural-panels" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Please add your name and a valid email.");
      return;
    }
    toast.success("Thank you — your enquiry has been received. Our team will be in touch.");
    setForm({ name: "", company: "", email: "", phone: "", quantity: "", location: "", notes: "" });
  };

  const input = "w-full border-b border-foreground/30 bg-transparent py-3 text-sm tracking-editorial outline-none transition-colors focus:border-foreground";

  return (
    <div data-testid="panel-detail" className="bg-white">
      <Breadcrumbs
        items={[
          { label: "Our Collection", to: "/collection" },
          { label: "Timber Collection", to: "/collections/timber" },
          { label: "Architectural Panels", to: "/collections/timber/architectural-panels" },
          { label: data.name },
        ]}
      />

      {/* Top */}
      <section className="edge pt-6 pb-16 md:pt-10" data-testid="panel-top">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="space-y-4 md:col-span-7">
            {/* Top profile image */}
            <div className="group relative aspect-[4/3] overflow-hidden border border-border/60 bg-white" data-testid="panel-sample-image">
              <img
                src={sampleDetail(data.slug)}
                alt={`${data.name} timber panel — top`}
                className="h-full w-full cursor-zoom-in object-contain transition-transform duration-[900ms] ease-out group-hover:scale-125"
                loading="lazy"
              />
            </div>
            {/* Side profile / cross-section */}
            <div data-testid="panel-cross-section">
              <span className="mb-2 block text-[0.72rem] uppercase tracking-[0.16em] text-muted-foreground">Side Profile</span>
              <div className="flex items-center justify-center border border-border/60 bg-white px-6 py-8">
                <img
                  src={crossSection(data.slug)}
                  alt={`${data.name} profile cross-section`}
                  className="mx-auto block max-h-28 w-auto max-w-full object-contain"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          <div className="md:col-span-5">
            <span className="eyebrow">PrimeOak® Timber Panel</span>
            <h1 className="mt-4 text-4xl font-light leading-[1.02] tracking-editorial md:text-5xl">{data.name}</h1>

            <dl className="mt-8 space-y-1">
              <div className="flex justify-between border-b border-border/60 py-3">
                <dt className="text-[0.72rem] uppercase tracking-[0.16em] text-muted-foreground">Choose your finish below</dt>
                <dd className="text-sm tracking-editorial" data-testid="selected-finish">{finish}</dd>
              </div>
              <div className="flex justify-between border-b border-border/60 py-3">
                <dt className="text-[0.72rem] uppercase tracking-[0.16em] text-muted-foreground">Dimensions</dt>
                <dd className="text-sm tracking-editorial">{data.dimensions}</dd>
              </div>
              <div className="flex justify-between border-b border-border/60 py-3">
                <dt className="text-[0.72rem] uppercase tracking-[0.16em] text-muted-foreground">Thickness</dt>
                <dd className="text-sm tracking-editorial">{data.thickness}</dd>
              </div>
            </dl>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button onClick={scrollToForm} data-testid="request-sample-btn" className="bg-primary px-6 py-3.5 text-sm uppercase tracking-[0.16em] text-primary-foreground transition-opacity hover:opacity-90">
                Request a Sample →
              </button>
              <button onClick={scrollToForm} data-testid="make-enquiry-btn" className="border border-foreground px-6 py-3.5 text-sm uppercase tracking-[0.16em] transition-colors hover:bg-foreground hover:text-background">
                Make an Enquiry
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 1. Finish */}
      <section className="edge py-16 border-t border-border/60" data-testid="panel-finish">
        <div className="flex items-baseline justify-between">
          <span className="eyebrow">01 — Choose Your Finish</span>
          <span className="text-xs text-muted-foreground" data-testid="finish-count">{FINISHES.length} colours</span>
        </div>
        <div className="mt-8">
          <ColourSwatches
            colours={FINISHES.map((f) => ({ name: f.name, image: finishImage(f.slug) }))}
            testid="finish-swatches"
            paginate
            perView={6}
            activeIndex={finishIdx}
            onSelect={setFinishIdx}
          />
        </div>
      </section>

      {/* 2. Specifications */}
      <section className="edge py-16 border-t border-border/60" data-testid="panel-specs">
        <span className="eyebrow">02 — Panel Specifications</span>
        <dl className="mt-8 max-w-3xl">
          <SpecRow label="Panel Type" value={data.panelType} />
          <SpecRow label="Profile Name" value={data.name} />
          <SpecRow label="Panel Dimensions" value={data.dimensions} />
          <SpecRow label="Available Thickness" value={data.thickness} />
          <SpecRow label="Timber Species" value="100% American Northern Red Oak" />
          <SpecRow label="Colour Finish" value="TBA" />
          <SpecRow label="Surface Style" value={data.surface} />
          <SpecRow label="Joint Type" value={data.joint} />
          <SpecRow label="One-Sided / Two-Sided" value="One Sided" />
        </dl>
      </section>

      {/* 4. Important Information */}
      <section className="edge py-14" data-testid="panel-disclaimer">
        <div className="max-w-3xl border-l-2 border-foreground bg-secondary/40 p-6">
          <span className="eyebrow">Important Information</span>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{PANEL_DISCLAIMER}</p>
        </div>
      </section>

      {/* 5. Enquiry Form */}
      <section ref={formRef} className="border-t border-border/60 bg-white" data-testid="panel-enquiry">
        <div className="edge py-20 md:py-28">
          <span className="eyebrow">Make an Enquiry</span>
          <h2 className="mt-4 text-3xl font-light tracking-editorial md:text-4xl">Enquire about {data.name}.</h2>
          <form onSubmit={submit} className="mt-12 grid max-w-3xl grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2" data-testid="enquiry-form">
            <div><label className="eyebrow">Full Name</label><input data-testid="enquiry-name" className={input} value={form.name} onChange={set("name")} /></div>
            <div><label className="eyebrow">Company</label><input data-testid="enquiry-company" className={input} value={form.company} onChange={set("company")} /></div>
            <div><label className="eyebrow">Email Address</label><input data-testid="enquiry-email" type="email" className={input} value={form.email} onChange={set("email")} /></div>
            <div><label className="eyebrow">Phone Number</label><input data-testid="enquiry-phone" className={input} value={form.phone} onChange={set("phone")} /></div>
            <div><label className="eyebrow">Selected Profile</label><input data-testid="enquiry-profile" className={`${input} text-foreground`} value={data.name} readOnly /></div>
            <div><label className="eyebrow">Selected Finish</label><input data-testid="enquiry-finish" className={`${input} text-foreground`} value={finish} readOnly /></div>
            <div><label className="eyebrow">Required Quantity / Project Size</label><input data-testid="enquiry-quantity" className={input} value={form.quantity} onChange={set("quantity")} /></div>
            <div className="sm:col-span-2"><label className="eyebrow">Project Location</label><input data-testid="enquiry-location" className={input} value={form.location} onChange={set("location")} /></div>
            <div className="sm:col-span-2"><label className="eyebrow">Additional Information</label><textarea data-testid="enquiry-notes" rows={4} className={input} value={form.notes} onChange={set("notes")} /></div>
            <div className="sm:col-span-2">
              <button type="submit" data-testid="enquiry-submit" className="bg-primary px-8 py-4 text-sm uppercase tracking-[0.16em] text-primary-foreground transition-opacity hover:opacity-90">
                Submit Enquiry →
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
